/**
 * Role-based access (legacy) + optional admin-managed tab paths on profiles.allowed_tab_paths.
 * Roles: admin, PM, team member, finance, HR
 */

export const ROLES = {
  ADMIN: 'admin',
  PM: 'pm',
  TEAM_MEMBER: 'team member',
  FINANCE: 'finance',
  HR: 'hr',
};

/** Same options as Team → Members edit/add user (profiles.role). */
export const ROLE_OPTIONS = [
  { value: ROLES.ADMIN, label: 'Admin' },
  { value: ROLES.HR, label: 'HR' },
  { value: ROLES.TEAM_MEMBER, label: 'Team member' },
  { value: ROLES.PM, label: 'PM' },
  { value: ROLES.FINANCE, label: 'Finance' },
];

/** Order for grouping manageable tab routes in Team members / profile editor. */
export const TAB_ROUTE_GROUPS = ['dept', 'hr', 'finance', 'other'];

export const TAB_ROUTE_GROUP_LABELS = {
  dept: 'Departments & projects',
  hr: 'HR & team',
  finance: 'Finance',
  other: 'Procurement, tenders & more',
};

/** Routes only administrators may open (ignores custom allowlists). */
export const ADMIN_ONLY_PATH_PREFIXES = [];

/** Sidebar/app routes admins can grant (dashboard & settings are always allowed when using custom lists). */
export const MANAGEABLE_ACCESS_ROUTES = [
  { path: '/departments', label: 'Departments', group: 'dept' },
  { path: '/departments/contacts', label: 'Contacts', group: 'dept' },
  { path: '/projects', label: 'Projects', group: 'dept' },
  { path: '/taskboard', label: 'Taskboard', group: 'dept' },
  { path: '/clients', label: 'Clients', group: 'dept' },
  { path: '/notes', label: 'Notes', group: 'dept' },
  { path: '/files', label: 'Files', group: 'dept' },
  { path: '/team/members', label: 'Team members', group: 'hr' },
  { path: '/hr/salary-chart', label: 'Salary chart', group: 'hr' },
  { path: '/sales/payments', label: 'Payments', group: 'hr' },
  { path: '/hr/leave-request', label: 'Leave request', group: 'hr' },
  { path: '/team/announcements', label: 'Announcements', group: 'hr' },
  { path: '/hr/payroll', label: 'Payroll', group: 'hr' },
  { path: '/hr/salary-advance', label: 'Salary advance', group: 'hr' },
  { path: '/hr/loans', label: 'Loans', group: 'hr' },
  { path: '/hr/rejoinings', label: 'Rejoinings', group: 'hr' },
  { path: '/team/time-cards', label: 'Time cards', group: 'hr' },
  { path: '/team/leave', label: 'Team leave', group: 'hr' },
  { path: '/expenses', label: 'Expenses', group: 'finance' },
  { path: '/sales/invoices', label: 'Invoices', group: 'finance' },
  { path: '/procurement', label: 'Procurement', group: 'other' },
  { path: '/tenders', label: 'Tenders & proposals', group: 'other' },
  { path: '/suppliers', label: 'Suppliers', group: 'other' },
  { path: '/purchase-orders', label: 'Purchase orders', group: 'other' },
  { path: '/logs', label: 'Logs', group: 'other' },
];

/** Grouped routes for optgroups / layout (User access page). */
export function getManageableRoutesGrouped() {
  const buckets = new Map(TAB_ROUTE_GROUPS.map((id) => [id, []]));
  for (const r of MANAGEABLE_ACCESS_ROUTES) {
    const g = r.group && buckets.has(r.group) ? r.group : 'other';
    buckets.get(g).push(r);
  }
  return TAB_ROUTE_GROUPS.map((id) => ({
    id,
    label: TAB_ROUTE_GROUP_LABELS[id],
    routes: buckets.get(id) || [],
  })).filter((section) => section.routes.length > 0);
}

/** Normalize role from DB (lowercase, trim). Unknown/null -> default. */
export function normalizeRole(role) {
  if (!role || typeof role !== 'string') return ROLES.TEAM_MEMBER;
  const r = role.trim().toLowerCase();
  if (Object.values(ROLES).includes(r)) return r;
  return ROLES.TEAM_MEMBER;
}

/** Normalize URL path for comparison. */
export function normalizePathname(pathname) {
  let path = (pathname || '').replace(/^#/, '').split('?')[0];
  if (path.length > 1) path = path.replace(/\/$/, '');
  return path || '/';
}

/**
 * Raw DB value for allowed_tab_paths:
 * - undefined (column absent): use legacy role map
 * - null: use legacy role map
 * - array (including []): custom allowlist only (+ / and /settings)
 */
export function resolveAllowedTabPaths(raw) {
  if (raw === undefined || raw === null) return 'legacy';
  if (Array.isArray(raw)) return raw.map(String);
  return 'legacy';
}

/** Paths (or prefixes) a role can access when profile has no custom allowlist. '*' means all. */
const ALLOWED_PATHS_BY_ROLE = {
  [ROLES.ADMIN]: ['*'],

  [ROLES.PM]: [
    '/',
    '/departments',
    '/projects',
    '/tasks',
    '/kanban',
    '/clients',
    '/notes',
    '/files',
    '/tenders',
    '/settings',
    '/logs',
  ],

  [ROLES.TEAM_MEMBER]: [
    '/',
    '/departments',
    '/projects',
    '/tasks',
    '/kanban',
    '/clients',
    '/notes',
    '/files',
    '/settings',
  ],

  [ROLES.FINANCE]: [
    '/',
    '/sales',
    '/expenses',
    '/suppliers',
    '/purchase-orders',
    '/tenders',
    '/settings',
  ],

  [ROLES.HR]: ['/', '/hr', '/team', '/sales/payments', '/settings'],
};

function pathMatchesAllowlist(path, prefixes) {
  return prefixes.some((p) => path === p || (p !== '/' && path.startsWith(`${p}/`)));
}

/**
 * When enabling custom sidebar paths, stored role should be admin (RLS). When disabling, reset role for others.
 * @param {boolean} useCustom - new "use custom tabs" value
 * @param {boolean} prevCustom - previous value
 * @param {string} prevRole - previous role string
 * @param {boolean} isSelf - editing own profile (keep role when turning custom off so admin can stay)
 */
export function applyTabRoleForCustomTabs(useCustom, prevCustom, prevRole, isSelf) {
  if (useCustom) return ROLES.ADMIN;
  if (prevCustom && !useCustom) {
    if (isSelf) return prevRole;
    return ROLES.TEAM_MEMBER;
  }
  return prevRole;
}

function legacyCanAccess(role, path) {
  const r = normalizeRole(role);
  if (ALLOWED_PATHS_BY_ROLE[r]?.includes('*')) return true;
  const allowed = ALLOWED_PATHS_BY_ROLE[r];
  if (!allowed) return false;
  return pathMatchesAllowlist(path, allowed);
}

/** Admin always has access to everything. */
export function isAdmin(role) {
  return normalizeRole(role) === ROLES.ADMIN;
}

/**
 * Check if a role can access a given pathname.
 * @param {string|null|undefined} role
 * @param {string} pathname
 * @param {null|undefined|string[]} [allowedTabPaths] — from profile.allowed_tab_paths; undefined = use legacy role map only
 *
 * When allowed_tab_paths is a JSON array (custom list), it applies to every role including admin:
 * use that for "admin in DB (RLS) but limited sidebar/routes in the app". When null/undefined, admins keep full app access.
 */
export function canAccess(role, pathname, allowedTabPaths) {
  const r = normalizeRole(role);
  const path = normalizePathname(pathname);

  const resolved = resolveAllowedTabPaths(allowedTabPaths);
  if (resolved !== 'legacy') {
    const always = ['/', '/settings'];
    const merged = [...new Set([...resolved, ...always])];
    return pathMatchesAllowlist(path, merged);
  }

  if (r === ROLES.ADMIN || ALLOWED_PATHS_BY_ROLE[r]?.includes('*')) return true;

  if (ADMIN_ONLY_PATH_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return r === ROLES.ADMIN;
  }

  return legacyCanAccess(r, path);
}

/**
 * Check if a role can access any of the given paths (used for sidebar links/groups).
 */
export function canAccessAny(role, paths, allowedTabPaths) {
  if (resolveAllowedTabPaths(allowedTabPaths) === 'legacy' && isAdmin(role)) return true;
  return paths.some((path) => canAccess(role, path, allowedTabPaths));
}
